import { useState, useEffect, useRef, useCallback } from 'react';
import { useSharedWS } from './useSocket';

export interface ChatSocketState {
    isConnected: boolean;
    connectionState: 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED';
    retryCount: number;
    lastError?: string;
}

interface ChatMessage {
    type: string;
    data?: unknown;
    content?: string;
    timestamp?: string;
    chunk?: any;
}

interface UseChatSocketOptions {
    url?: string;
    sessionId?: string;
    userId?: string;
    onMessage?: (message: ChatMessage) => void;
    onChatEcho?: (message: unknown) => void;
    onBeeLogChunk?: (chunk: any) => void;
    onConnectionChange?: (isConnected: boolean) => void;
    onOpen?: () => void;
    onError?: (error: string) => void;
}

export function useChatSocket(options: UseChatSocketOptions): ChatSocketState & {
    sendMessage: (message: ChatMessage) => void;
    disconnect: () => void;
    status: 'idle' | 'connecting' | 'open' | 'error';
} {
    const {
        sessionId,
        userId,
        onMessage,
        onChatEcho,
        onBeeLogChunk,
        onConnectionChange,
        onOpen,
        onError,
    } = options;

    const [state, setState] = useState<ChatSocketState>({
        isConnected: false,
        connectionState: 'CONNECTING',
        retryCount: 0,
    });

    const [status, setStatus] = useState<'idle' | 'connecting' | 'open' | 'error'>('connecting');

    const onMessageRef = useRef(onMessage);
    const onChatEchoRef = useRef(onChatEcho);
    const onBeeLogChunkRef = useRef(onBeeLogChunk);
    const onConnectionChangeRef = useRef(onConnectionChange);
    const onOpenRef = useRef(onOpen);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onMessageRef.current = onMessage;
        onChatEchoRef.current = onChatEcho;
        onBeeLogChunkRef.current = onBeeLogChunk;
        onConnectionChangeRef.current = onConnectionChange;
        onOpenRef.current = onOpen;
        onErrorRef.current = onError;
    });

    const log = useCallback((message: string, level: 'info' | 'warn' | 'error' = 'info') => {
        const timestamp = new Date().toISOString();
        const prefix = `[CHAT-SOCKET]`;
        console[level](`${timestamp} ${prefix} ${message}`);
    }, []);

    const handleMessage = useCallback(
        (event: MessageEvent) => {
            try {
                const message = JSON.parse(event.data);

                switch (message.type) {
                    case 'pong':
                        log('Received pong');
                        break;
                    case 'chat_echo':
                        onChatEchoRef.current?.(message);
                        break;
                    case 'beeLogChunk':
                        onBeeLogChunkRef.current?.(message.chunk);
                        break;
                    default:
                        onMessageRef.current?.(message);
                        break;
                }
            } catch (error) {
                log(`Error parsing message: ${error}`, 'error');
            }
        },
        [log]
    );

    const { send, socket } = useSharedWS('/chat', handleMessage);

    useEffect(() => {
        if (socket?.readyState === WebSocket.OPEN) {
            log('Connected successfully');
            setState(prev => ({
                ...prev,
                isConnected: true,
                connectionState: 'OPEN',
                retryCount: 0,
                lastError: undefined,
            }));
            setStatus('open');
            onConnectionChangeRef.current?.(true);
            onOpenRef.current?.();
        } else if (socket?.readyState === WebSocket.CONNECTING) {
            setState(prev => ({ ...prev, connectionState: 'CONNECTING' }));
            setStatus('connecting');
        } else {
            setState(prev => ({
                ...prev,
                isConnected: false,
                connectionState: 'CLOSED',
            }));
            setStatus('error');
            onConnectionChangeRef.current?.(false);
        }
    }, [socket, log]);

    const sendMessage = useCallback(
        (message: ChatMessage) => {
            send(message);
        },
        [send]
    );

    const disconnect = useCallback(() => {
        log('Manually disconnecting');
    }, [log]);

    return {
        ...state,
        sendMessage,
        disconnect,
        status,
    };
}
